import type {
  AnyFailure,
  Person,
  PersonListResponse,
  PersonListSuccess,
} from "@b2bportal/air-booking-api";

import { trackCheckoutEvent } from "@checkout/tracking/sink";
import { PassengerErrorModalTypes } from "@checkout/types";
import { fetchUserPassengers, updateUserPassenger } from "@hopper-b2b/api";
import { ADD_PASSPORT, SAVE_TRAVELER } from "@hopper-b2b/types";
import { FlightPassengerSelectorsV2 } from "..";
import { getTripCategoryParent } from "../../../selectors";
import type { FlightContextV2 } from "../../../types";

async function preserveExistingFields(person: Person): Promise<Person> {
  if (person.passport && person.driverLicense) return person;
  try {
    const response = await fetchUserPassengers({});
    const existing = (response as PersonListSuccess).value?.find(
      (p: Person) => p.id === person.id
    );
    if (existing) {
      return {
        ...person,
        passport: person.passport || existing.passport,
        driverLicense: person.driverLicense || existing.driverLicense,
      };
    }
  } catch (error) {
    console.warn(
      `Failed to fetch existing passenger data for field preservation (passenger ID: ${person.id}):`,
      error
    );
  }
  return person;
}

export const updateUserPassengerService = (
  context: FlightContextV2,
  event: { person: Person; onUpdate?: boolean }
) =>
  new Promise((resolve, reject) => {
    const { person, onUpdate } = event;

    const { passport, driverLicense, ...restOfPayload } = person;
    const personToUpdate: Person = { ...restOfPayload };
    if (passport?.countryOfIssue?.length === 2) {
      personToUpdate.passport = passport;
    }
    const addPassport = FlightPassengerSelectorsV2.getAddPassport({ context });
    const tripCategory = getTripCategoryParent({
      context,
    });

    preserveExistingFields(personToUpdate)
      .then((mergedPerson) => updateUserPassenger({ person: mergedPerson }))
      .then(() => {
        trackCheckoutEvent({
          eventName: SAVE_TRAVELER,
          properties: { trip_type: tripCategory },
        });
        fetchUserPassengers({}).then((response: PersonListResponse) => {
          if (addPassport) {
            trackCheckoutEvent({
              eventName: ADD_PASSPORT,
              properties: { success: false },
            });
          }
          if ((response as AnyFailure).Response === "Failure") {
            reject({
              type: PassengerErrorModalTypes.UpdatePassenger,
              data: (response as AnyFailure).errors,
            });
          } else {
            resolve({
              id: personToUpdate.id,
              value: (response as PersonListSuccess).value,
              onUpdate,
            });
          }
        });
      })
      .catch((e) => {
        if (addPassport) {
          trackCheckoutEvent({
            eventName: ADD_PASSPORT,
            properties: { success: false },
          });
        }
        reject({
          type: PassengerErrorModalTypes.UpdatePassenger,
          data: e,
        });
      });
  });
